import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
} from '@mui/material';
import { People, AttachMoney, VolunteerActivism, Group } from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  getMothers,
  getDonors,
  getPartners,
  getDonorView,
  getAllMonthlySavings,
} from './api';

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600px
  const isLaptop = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 959px

  const [stats, setStats] = useState({
    totalMothers: 0,
    totalSavings: 0,
    totalDonors: 0,
    totalContributions: 0,
    totalPartners: 0,
  });
  const [mothersPerPartner, setMothersPerPartner] = useState({ labels: [], data: [] });
  const [mothersPerActivity, setMothersPerActivity] = useState({ labels: [], data: [] });
  const [activitiesPerPartner, setActivitiesPerPartner] = useState({ labels: [], data: [] });
  const [savingsTrend, setSavingsTrend] = useState({ labels: [], data: [] });
  const [donationTrend, setDonationTrend] = useState({ labels: [], data: [] });
  const [monthlyActivitiesPerPartner, setMonthlyActivitiesPerPartner] = useState({ labels: [], series: [] });
  const [donorRegistrationTrend, setDonorRegistrationTrend] = useState({ labels: [], data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeFilter, setTimeFilter] = useState('6m'); // Default to last 6 months

  const timeFilterOptions = [
    { value: '6m', label: 'Last 6 Months' },
    { value: '12m', label: 'Last 12 Months' },
    { value: 'all', label: 'All Time' },
  ];

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const [mothersResponse, donorsResponse, partnersResponse, donorViewResponse, monthlySavingsResponse] = await Promise.all([
          getMothers(),
          getDonors(),
          getPartners(),
          getDonorView(),
          getAllMonthlySavings(),
        ]);

        const mothers = Array.isArray(mothersResponse) ? mothersResponse : [];
        const donors = Array.isArray(donorsResponse) ? donorsResponse : [];
        const partners = Array.isArray(partnersResponse) ? partnersResponse : [];
        const donorView = donorViewResponse.donor_view || [];
        const monthlySavings = Array.isArray(monthlySavingsResponse) ? monthlySavingsResponse : [];

        // Calculate total savings and contributions
        const totalSavings = mothers.reduce((sum, mother) => sum + (mother.total_monthly_savings || 0), 0);
        const totalContributions = donorView.reduce((sum, mother) => sum + (mother.total_donor_contributions || 0), 0);

        // Mothers per Partner using mother_count
        const partnerLabels = partners.map((partner) => partner.partner_name || `Partner ${partner.partner_id}`);
        const mothersPerPartnerData = partners.map((partner) => partner.mother_count || 0);

        // Mothers per Activity
        const mothersByActivity = {};
        mothers.forEach((mother) => {
          (mother.activities || []).forEach((activity) => {
            mothersByActivity[activity.name] = (mothersByActivity[activity.name] || 0) + 1;
          });
        });
        const activityLabels = Object.keys(mothersByActivity);
        const mothersPerActivityData = Object.values(mothersByActivity);

        // Activities per Partner
        const activitiesByPartner = {};
        partners.forEach((partner) => {
          activitiesByPartner[partner.partner_id] = partner.activities ? partner.activities.length : 0;
        });
        const activitiesPerPartnerData = partners.map((partner) => activitiesByPartner[partner.partner_id] || 0);

        // Savings and Donation Trends
        const savingsByMonth = {};
        const donationsByMonth = {};
        monthlySavings.forEach((saving) => {
          savingsByMonth[saving.month_key] = (savingsByMonth[saving.month_key] || 0) + (saving.savings || 0);
          donationsByMonth[saving.month_key] = (donationsByMonth[saving.month_key] || 0) + (saving.donor_contribution || 0);
        });

        // Monthly Activities per Partner
        const activitiesByPartnerMonth = {};
        partners.forEach((partner) => {
          activitiesByPartnerMonth[partner.partner_id] = {};
          (partner.activities || []).forEach((activity) => {
            const monthKey = activity.month_key || (activity.date ? activity.date.slice(0, 7) : 'Unknown');
            if (monthKey !== 'Unknown') {
              activitiesByPartnerMonth[partner.partner_id][monthKey] =
                (activitiesByPartnerMonth[partner.partner_id][monthKey] || 0) + 1;
            }
          });
        });

        // Donor Registration Trends
        const registrationsByMonth = {};
        donors.forEach((donor) => {
          const monthKey = donor.registration_date ? donor.registration_date.slice(0, 7) : 'Unknown';
          if (monthKey !== 'Unknown') {
            registrationsByMonth[monthKey] = (registrationsByMonth[monthKey] || 0) + 1;
          }
        });

        // Filter by time period
        const now = new Date();
        const filterDate = new Date();
        if (timeFilter === '6m') {
          filterDate.setMonth(now.getMonth() - 6);
        } else if (timeFilter === '12m') {
          filterDate.setMonth(now.getMonth() - 12);
        }
        const filteredMonths = Object.keys(savingsByMonth)
          .filter((month) => timeFilter === 'all' || new Date(month + '-01') >= filterDate)
          .sort();
        const savingsData = filteredMonths.map((month) => savingsByMonth[month] || 0);
        const donationData = filteredMonths.map((month) => donationsByMonth[month] || 0);
        const registrationData = filteredMonths.map((month) => registrationsByMonth[month] || 0);
        const activitySeries = partners.map((partner) => ({
          name: partner.partner_name || `Partner ${partner.partner_id}`,
          data: filteredMonths.map((month) => activitiesByPartnerMonth[partner.partner_id]?.[month] || 0),
        }));

        setStats({
          totalMothers: mothers.length,
          totalSavings,
          totalDonors: donors.length,
          totalContributions,
          totalPartners: partners.length,
        });
        setMothersPerPartner({ labels: partnerLabels, data: mothersPerPartnerData });
        setMothersPerActivity({ labels: activityLabels, data: mothersPerActivityData });
        setActivitiesPerPartner({ labels: partnerLabels, data: activitiesPerPartnerData });
        setSavingsTrend({ labels: filteredMonths, data: savingsData });
        setDonationTrend({ labels: filteredMonths, data: donationData });
        setMonthlyActivitiesPerPartner({ labels: filteredMonths, series: activitySeries });
        setDonorRegistrationTrend({ labels: filteredMonths, data: registrationData });
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [timeFilter]);

  const handleTimeFilterChange = (event) => {
    setTimeFilter(event.target.value);
  };

  const StatCard = ({ title, value, icon, color, description }) => (
    <Card sx={{ 
      borderRadius: 3, 
      boxShadow: '0 8px 32px rgba(255,105,180,0.12)',
      background: color,
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      flex: '1 1 250px',
      minWidth: '250px',
      maxWidth: '100%',
    }}>
      <Box sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
      }} />
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
            {loading ? '...' : value}
          </Typography>
          {icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Highcharts Configurations
  const mothersPerPartnerChart = {
    chart: { type: 'column', height: isMobile || isLaptop ? 250 : 300 },
    title: { text: 'Mothers per Partner', style: { color: '#333', fontSize: isMobile || isLaptop ? '12px' : '16px' } },
    xAxis: { categories: mothersPerPartner.labels, title: { text: 'Partners' }, labels: { rotation: -45, style: { fontSize: isMobile || isLaptop ? '8px' : '10px' } } },
    yAxis: { title: { text: 'Number of Mothers' }, allowDecimals: false },
    series: [{ name: 'Mothers', data: mothersPerPartner.data, color: '#ff6f61' }],
    credits: { enabled: false },
    tooltip: { backgroundColor: '#1d3557', style: { color: '#fff' } },
    plotOptions: { column: { borderColor: '#e63946', borderWidth: 1 } },
    responsive: {
      rules: [{ condition: { maxWidth: 500 }, chartOptions: { legend: { enabled: false }, xAxis: { labels: { rotation: -45 } } } }],
    },
  };

  const mothersPerActivityChart = {
    chart: { type: 'pie', height: isMobile || isLaptop ? 250 : 300 },
    title: { text: 'Mothers per Activity', style: { color: '#333', fontSize: isMobile || isLaptop ? '12px' : '16px' } },
    series: [{
      name: 'Mothers',
      data: mothersPerActivity.labels.map((label, i) => ({
        name: label,
        y: mothersPerActivity.data[i],
      })),
      colors: ['#ff6f61', '#6b7280', '#f4a261', '#2a9d8f', '#e63946'],
    }],
    credits: { enabled: false },
    tooltip: { backgroundColor: '#1d3557', style: { color: '#fff' } },
    plotOptions: { pie: { allowPointSelect: true, cursor: 'pointer', dataLabels: { enabled: true, format: '{point.name}: {point.percentage:.1f}%', style: { fontSize: isMobile || isLaptop ? '8px' : '10px' } } } },
    responsive: {
      rules: [{ condition: { maxWidth: 500 }, chartOptions: { legend: { align: 'center', verticalAlign: 'bottom', layout: 'horizontal', itemStyle: { fontSize: '8px' } } } }],
    },
  };

  const activitiesPerPartnerChart = {
    chart: { type: 'column', height: isMobile || isLaptop ? 250 : 300 },
    title: { text: 'Activities per Partner', style: { color: '#333', fontSize: isMobile || isLaptop ? '12px' : '16px' } },
    xAxis: { categories: activitiesPerPartner.labels, title: { text: 'Partners' }, labels: { rotation: -45, style: { fontSize: isMobile || isLaptop ? '8px' : '10px' } } },
    yAxis: { title: { text: 'Number of Activities' }, allowDecimals: false },
    series: [{ name: 'Activities', data: activitiesPerPartner.data, color: '#2a9d8f' }],
    credits: { enabled: false },
    tooltip: { backgroundColor: '#1d3557', style: { color: '#fff' } },
    plotOptions: { column: { borderColor: '#1d3557', borderWidth: 1 } },
    responsive: {
      rules: [{ condition: { maxWidth: 500 }, chartOptions: { legend: { enabled: false }, xAxis: { labels: { rotation: -45 } } } }],
    },
  };

  const savingsDonationTrendChart = {
    chart: { type: 'line', height: isMobile || isLaptop ? 250 : 300 },
    title: { text: 'Savings and Donation Trends', style: { color: '#333', fontSize: isMobile || isLaptop ? '12px' : '16px' } },
    xAxis: { categories: savingsTrend.labels, title: { text: 'Month' }, labels: { style: { fontSize: isMobile || isLaptop ? '8px' : '10px' } } },
    yAxis: { title: { text: 'Amount (UGX)' }, allowDecimals: false },
    series: [
      { name: 'Savings', data: savingsTrend.data, color: '#ff6f61', lineWidth: 2 },
      { name: 'Donations', data: donationTrend.data, color: '#2a9d8f', lineWidth: 2 },
    ],
    credits: { enabled: false },
    tooltip: { backgroundColor: '#1d3557', style: { color: '#fff' } },
    plotOptions: { line: { marker: { enabled: true, radius: 3 } } },
    responsive: {
      rules: [{ condition: { maxWidth: 500 }, chartOptions: { legend: { align: 'center', verticalAlign: 'bottom', itemStyle: { fontSize: '8px' } } } }],
    },
  };

  const monthlyActivitiesPerPartnerChart = {
    chart: { type: 'line', height: isMobile || isLaptop ? 250 : 300 },
    title: { text: 'Monthly Activities per Partner', style: { color: '#333', fontSize: isMobile || isLaptop ? '12px' : '16px' } },
    xAxis: { categories: monthlyActivitiesPerPartner.labels, title: { text: 'Month' }, labels: { style: { fontSize: isMobile || isLaptop ? '8px' : '10px' } } },
    yAxis: { title: { text: 'Number of Activities' }, allowDecimals: false },
    series: monthlyActivitiesPerPartner.series,
    credits: { enabled: false },
    tooltip: { backgroundColor: '#1d3557', style: { color: '#fff' } },
    plotOptions: { line: { marker: { enabled: true, radius: 3 } } },
    responsive: {
      rules: [{ condition: { maxWidth: 500 }, chartOptions: { legend: { align: 'center', verticalAlign: 'bottom', itemStyle: { fontSize: '8px' } } } }],
    },
  };

  const donorRegistrationTrendChart = {
    chart: { type: 'line', height: isMobile || isLaptop ? 250 : 300 },
    title: { text: 'Donor Registration Trends', style: { color: '#333', fontSize: isMobile || isLaptop ? '12px' : '16px' } },
    xAxis: { categories: donorRegistrationTrend.labels, title: { text: 'Month' }, labels: { style: { fontSize: isMobile || isLaptop ? '8px' : '10px' } } },
    yAxis: { title: { text: 'Number of Registrations' }, allowDecimals: false },
    series: [{ name: 'Registrations', data: donorRegistrationTrend.data, color: '#f4a261', lineWidth: 2 }],
    credits: { enabled: false },
    tooltip: { backgroundColor: '#1d3557', style: { color: '#fff' } },
    plotOptions: { line: { marker: { enabled: true, radius: 3 } } },
    responsive: {
      rules: [{ condition: { maxWidth: 500 }, chartOptions: { legend: { align: 'center', verticalAlign: 'bottom', itemStyle: { fontSize: '8px' } } } }],
    },
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4, background: '#f5f5f5', minHeight: '100vh', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 0 }}>
        
        <FormControl sx={{ minWidth: 150 ,p:4,m:4}}>
          <InputLabel>Time Period</InputLabel>
          <Select value={timeFilter} onChange={handleTimeFilterChange} label="Time Period">
            {timeFilterOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {/* Stat Cards Section at the Top */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: 2, 
            mb: 4, 
            width: '100%',
            flexWrap: 'wrap'
          }}>
            <StatCard
              title="Total Mothers"
              value={stats.totalMothers}
              icon={<People sx={{ fontSize: 40, opacity: 0.8 }} />}
              color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
              description="Registered across all partners"
            />
            <StatCard
              title="Total Savings"
              value={`$${stats.totalSavings.toFixed(2)}`}
              icon={<AttachMoney sx={{ fontSize: 40, opacity: 0.8 }} />}
              color="linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)"
              description="Accumulated by all mothers"
            />
            <StatCard
              title="Total Donors"
              value={stats.totalDonors}
              icon={<VolunteerActivism sx={{ fontSize: 40, opacity: 0.8 }} />}
              color="linear-gradient(135deg, #2196F3 0%, #1976D2 100%)"
              description="Supporting mothers worldwide"
            />
            <StatCard
              title="Total Contributions"
              value={`$${stats.totalContributions.toFixed(2)}`}
              icon={<AttachMoney sx={{ fontSize: 40, opacity: 0.8 }} />}
              color="linear-gradient(135deg, #FF9800 0%, #F57C00 100%)"
              description="From generous donors"
            />
            <StatCard
              title="Total Partners"
              value={stats.totalPartners}
              icon={<Group sx={{ fontSize: 40, opacity: 0.8 }} />}
              color="linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)"
              description="Organizations in the network"
            />
          </Box>

          {/* Graph Cards Section */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)', // Always 2 cards per row
              gap: isMobile ? 2 : 3,
              width: '100%',
            }}
          >
            {[
              mothersPerPartnerChart,
              mothersPerActivityChart,
              activitiesPerPartnerChart,
              savingsDonationTrendChart,
              monthlyActivitiesPerPartnerChart,
              donorRegistrationTrendChart
            ].map((chart, index) => (
              <Paper
                key={index}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  boxShadow: '0 6px 30px rgba(0,0,0,0.08)',
                  height: '100%',
                  width: '100%',
                  background: '#f92955',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 36px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <HighchartsReact highcharts={Highcharts} options={chart} />
              </Paper>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default Dashboard;